import { memo, type FC } from "react";
import {
  Box,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Switch,
  Typography,
} from "@mui/material";
import type { CompanyFilters } from "../types";
import type { FilterModule } from "./index";
import { useCompanyFilterSlice, useCompanyFilterActions } from "../hooks";

type ContactModeSlice = {
  mode: "filter_company_list" | "export_contacts_only";
  primaryCompanyOnly: boolean;
};

// OppGen's default (`DEFAULT_CONTACT_MODE`): contact criteria also narrow the
// exported company list unless the user opts into "Export contacts only".
const defaultSlice: ContactModeSlice = {
  mode: "filter_company_list",
  primaryCompanyOnly: false,
};

const ContactModeFilter: FC = memo(() => {
  const slice = useCompanyFilterSlice("contactMode");
  const { setSlice } = useCompanyFilterActions();

  const update = (patch: Partial<ContactModeSlice>) =>
    setSlice("contactMode", { ...slice, ...patch });

  return (
    <Box sx={{ p: 1 }}>
      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ fontStyle: "italic" }}
      >
        Applies when contact filters are active
      </Typography>
      <RadioGroup
        value={slice.mode}
        onChange={(_, v) => update({ mode: v as ContactModeSlice["mode"] })}
      >
        <FormControlLabel
          value="filter_company_list"
          control={
            <Radio
              size="small"
              data-testid="contacts-mode-filter-company-radio"
            />
          }
          label={
            <Box>
              <Typography variant="body2">Filter company list</Typography>
              <Typography variant="caption" color="textSecondary">
                Only show companies with matching contacts
              </Typography>
            </Box>
          }
        />
        <FormControlLabel
          value="export_contacts_only"
          control={
            <Radio size="small" data-testid="contacts-mode-export-only-radio" />
          }
          label={
            <Box>
              <Typography variant="body2">Export contacts only</Typography>
              <Typography variant="caption" color="textSecondary">
                Apply to contact export, not company list
              </Typography>
            </Box>
          }
        />
      </RadioGroup>
      <Divider sx={{ my: 1 }} />
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={slice.primaryCompanyOnly}
            onChange={(e) => update({ primaryCompanyOnly: e.target.checked })}
            data-testid="contacts-primary-company-switch"
          />
        }
        label={
          <Typography variant="body2">
            Deduplicate contacts and assign to corporate parent
          </Typography>
        }
      />
    </Box>
  );
});

ContactModeFilter.displayName = "ContactModeFilter";

export const contactModeModule: FilterModule<ContactModeSlice> = {
  key: "contactMode",
  defaultSlice,
  Component: ContactModeFilter,
  // Emit nothing at the default so an untouched module leaves
  // `appliedFilters` undefined (the "no filters" state the page relies on).
  toCompanyFilters: (slice: ContactModeSlice): Partial<CompanyFilters> =>
    slice.mode === defaultSlice.mode &&
    slice.primaryCompanyOnly === defaultSlice.primaryCompanyOnly
      ? {}
      : {
          contactFilters: {
            mode: slice.mode,
            primaryCompanyOnly: slice.primaryCompanyOnly,
          },
        },
  fromCompanyFilters: (filters: CompanyFilters): ContactModeSlice => ({
    mode: filters.contactFilters?.mode ?? defaultSlice.mode,
    primaryCompanyOnly:
      filters.contactFilters?.primaryCompanyOnly ??
      defaultSlice.primaryCompanyOnly,
  }),
  getChips: () => [],
  getCount: (slice) => (slice.primaryCompanyOnly ? 1 : 0),
};
